'use client';

import { useDebouncedState } from '@mantine/hooks';
import * as Popover from '@radix-ui/react-popover';
import { FaUser } from 'react-icons/fa';
import { twJoin } from 'tailwind-merge';

import { Button } from '@/components/Button';
import { clsx } from '@/utils/helpers';
import { Paths, ZmyleLinks } from '@/utils/paths';

import { AccountMenuItem } from './AccountMenuItem';

export type SignupMenuProps = {
  content: LayoutContent;
};

export function SignupMenu({ content }: SignupMenuProps) {
  const [isOpen, setIsOpen] = useDebouncedState(false, 200);

  const { login, signUp, asBenefit, asNetwork, asStore } = content.topbar;

  const origin = typeof window !== 'undefined' ? window.location.origin : '';

  function onMouseEnter() {
    setIsOpen(true);
  }

  function onMouseLeave() {
    setIsOpen(false);
  }

  return (
    <Popover.Root open={isOpen} onOpenChange={setIsOpen}>
      <Popover.Trigger asChild>
        <Button
          className={clsx(
            'aspect-square rounded-full p-2 font-medium',
            'hover:text-zmyle-primary hover:bg-gray-50',
          )}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
          variant="ghost"
          aria-label="Sign up menu"
        >
          <FaUser className="size-4" />
        </Button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          sideOffset={12}
          className={twJoin(
            'relative z-15 flex w-48 flex-col gap-2 py-2',
            'rounded-lg bg-white shadow-lg',
            'border border-gray-100',
            'outline-none',
          )}
          side="bottom"
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
        >
          <Popover.Arrow
            className="fill-white stroke-gray-100 stroke-1"
            width={15}
            height={10}
          />

          <AccountMenuItem href={`${origin}/${ZmyleLinks.LOGIN}`} isExternal>
            {login}
          </AccountMenuItem>

          <hr className="border-t border-gray-200" />

          <div className="flex flex-col">
            <p className="w-full px-4 py-2 text-left">{signUp}</p>

            <AccountMenuItem
              href={`${origin}/${ZmyleLinks.SIGN_UP_STORE}`}
              isExternal
            >
              {asStore}
            </AccountMenuItem>

            <AccountMenuItem
              href={`${origin}/${ZmyleLinks.SIGN_UP_COMPANY}`}
              isExternal
            >
              {asBenefit}
            </AccountMenuItem>

            <AccountMenuItem href={Paths.REGISTER_NETWORK} target="_self">
              {asNetwork}
            </AccountMenuItem>
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
